import React from 'react';
import PropTypes from 'prop-types';
import { Field, useField, useFormState } from 'react-final-form';
import { Box, Flex } from 'rebass';
import styled from 'styled-components';
import dayjs from 'dayjs';

import { Trans, t } from '@lingui/macro';
import { Typography } from '@oca/ui';
import {
  SelectWrapper,
  TextFieldWrapper,
  RadioGroupWrapper,
  SwitchFieldWrapper,
  DayPickerInputWrapper,
  DATE_FIELD_SUBSCRIPTION,
} from '@lib/final-form-oca-ui';
import { i18n } from '@lib/i18n';

import {
  recurrenceFrequencies,
  weekDays,
  monthlyModes,
  monthlyWeeks,
  endTypes,
} from '../datas';
import { getRecurrencePreview } from '../lib/recurrence';

const toInt = value => (value === '' || value == null ? '' : Number(value));

// Stable reference: an inline options object makes react-flatpickr re-init the
// picker on every recurrence re-render (the section re-renders for the live
// preview), which would swallow the first click on the end-date field.
const END_DATE_OPTIONS = {
  minDate: 'today',
  formatDate: date => dayjs(date).format('ll'),
};

// Generic select helpers for {value,label} option lists with primitive values.
const optionValue = option => option.value;
const optionLabel = option => option.label;
const parseOption = option => (option ? option.value : option);
const formatOption = options => value =>
  options.find(option => option.value === value) || null;

function intervalUnit(frequency) {
  if (frequency === 'weekly') return i18n._(t`weeks`);
  if (frequency === 'monthly') return i18n._(t`months`);
  return i18n._(t`days`);
}

/**
 * "Repeat" toggle and, when enabled, the full recurrence configuration with a
 * live preview. Bound to form fields `repeat` (UI-only) and `recurrence.*`.
 */
export function RecurrenceFields() {
  const { input } = useField('repeat', { subscription: { value: true } });
  const isOn = !!input.value;

  return (
    <Box mb={25}>
      <Flex alignItems="center" justifyContent="space-between">
        <Typography variant="body1" color="text">
          <Trans>Repeat</Trans>
        </Typography>
        <Field name="repeat" type="checkbox" component={SwitchFieldWrapper} />
      </Flex>
      {isOn && <RecurrenceConfig />}
    </Box>
  );
}

function RecurrenceConfig() {
  const { values } = useFormState({ subscription: { values: true } });
  const recurrence = (values && values.recurrence) || {};
  const { frequency, monthlyMode, endType } = recurrence;
  const preview = getRecurrencePreview(recurrence);

  return (
    <ConfigBox mt={3}>
      <Field
        name="recurrence.frequency"
        label={<Trans>Frequency</Trans>}
        spaceAfter
        helperText=" "
        options={recurrenceFrequencies}
        getOptionValue={optionValue}
        getOptionLabel={optionLabel}
        parse={parseOption}
        format={formatOption(recurrenceFrequencies)}
        component={SelectWrapper}
      />

      <Flex alignItems="center" mb={3}>
        <Typography variant="body2" color="text">
          <Trans>Repeat every</Trans>
        </Typography>
        <Box width={72} mx={2}>
          <Field
            name="recurrence.interval"
            type="number"
            min={1}
            parse={toInt}
            component={TextFieldWrapper}
          />
        </Box>
        <Typography variant="body2" color="text">
          {intervalUnit(frequency)}
        </Typography>
      </Flex>

      {frequency === 'weekly' && (
        <Box mb={3}>
          <SectionLabel>
            <Trans>Repeat on</Trans>
          </SectionLabel>
          <Field
            name="recurrence.weeklyDays"
            subscription={{ value: true, error: true, touched: true }}
          >
            {({ input, meta }) => (
              <>
                <WeekdayPicker
                  value={input.value || []}
                  onChange={input.onChange}
                />
                {meta.touched && meta.error && (
                  <Typography variant="body2" color="error">
                    {meta.error}
                  </Typography>
                )}
              </>
            )}
          </Field>
        </Box>
      )}

      {frequency === 'monthly' && (
        <Box mb={3}>
          <Box mb={2}>
            <Field
              name="recurrence.monthlyMode"
              options={monthlyModes}
              component={RadioGroupWrapper}
            />
          </Box>
          {monthlyMode === 'weekday_pattern' ? (
            <Flex mx={-2}>
              <Box width={1 / 2} px={2}>
                <Field
                  name="recurrence.monthlyWeek"
                  options={monthlyWeeks}
                  getOptionValue={optionValue}
                  getOptionLabel={optionLabel}
                  parse={parseOption}
                  format={formatOption(monthlyWeeks)}
                  component={SelectWrapper}
                />
              </Box>
              <Box width={1 / 2} px={2}>
                <Field
                  name="recurrence.monthlyWeekday"
                  options={weekDays}
                  getOptionValue={optionValue}
                  getOptionLabel={optionLabel}
                  parse={parseOption}
                  format={formatOption(weekDays)}
                  component={SelectWrapper}
                />
              </Box>
            </Flex>
          ) : (
            <Box width={120}>
              <Field
                name="recurrence.monthlyDay"
                label={<Trans>Day of month</Trans>}
                type="number"
                min={1}
                max={31}
                helperText=" "
                parse={toInt}
                component={TextFieldWrapper}
              />
            </Box>
          )}
        </Box>
      )}

      <SectionLabel>
        <Trans>Ends</Trans>
      </SectionLabel>
      <Box mb={2}>
        <Field
          name="recurrence.endType"
          options={endTypes}
          direction="row"
          component={RadioGroupWrapper}
        />
      </Box>

      {endType === 'on_date' && (
        <Field
          type="date"
          name="recurrence.endDate"
          subscription={DATE_FIELD_SUBSCRIPTION}
          label={<Trans>End date</Trans>}
          spaceAfter
          helperText=" "
          format={value => (value ? new Date(value) : null)}
          options={END_DATE_OPTIONS}
          component={DayPickerInputWrapper}
        />
      )}

      {endType === 'after_occurrences' && (
        <Box width={120}>
          <Field
            name="recurrence.occurrences"
            label={<Trans>Occurrences</Trans>}
            type="number"
            min={1}
            helperText=" "
            parse={toInt}
            component={TextFieldWrapper}
          />
        </Box>
      )}

      {preview && (
        <PreviewBox mt={2}>
          <Typography variant="body2" color="textSecondary">
            {preview}
          </Typography>
        </PreviewBox>
      )}
    </ConfigBox>
  );
}

function WeekdayPicker({ value, onChange }) {
  const selected = new Set((value || []).map(Number));

  const toggle = day => {
    const next = new Set(selected);
    if (next.has(day)) {
      next.delete(day);
    } else {
      next.add(day);
    }
    onChange([...next].sort((a, b) => a - b));
  };

  return (
    <Flex flexWrap="wrap">
      {weekDays.map(day => (
        <DayButton
          type="button"
          key={day.value}
          active={selected.has(day.value)}
          onClick={() => toggle(day.value)}
        >
          {day.short}
        </DayButton>
      ))}
    </Flex>
  );
}

WeekdayPicker.propTypes = {
  value: PropTypes.arrayOf(PropTypes.number),
  onChange: PropTypes.func.isRequired,
};

WeekdayPicker.defaultProps = {
  value: [],
};

const ConfigBox = styled(Box)`
  padding: 16px;
  border-radius: 8px;
  border: 1px solid #eef0f4;
  background-color: #fafbfc;
`;

const SectionLabel = styled.span`
  display: block;
  margin-bottom: 8px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const DayButton = styled.button`
  width: 40px;
  height: 36px;
  margin-right: 6px;
  margin-bottom: 6px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  border: 1px solid ${({ active }) => (active ? '#0075f4' : '#d0d5dd')};
  background-color: ${({ active }) => (active ? '#0075f4' : '#fff')};
  color: ${({ active }) => (active ? '#fff' : '#475467')};
  transition: background-color 120ms ease, border-color 120ms ease;
`;

const PreviewBox = styled(Box)`
  padding: 10px 12px;
  border-radius: 6px;
  background-color: #eef4ff;
`;
